import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormSelectInput,
} from '@korint/folio';
import {
  type AxeceDrivequantSurchargeRate,
  useAxeceControllerSurchargePolicy,
  axeceDrivequantSurchargeRateEnum,
} from 'korint-sdk';
import { useApiCallback } from 'korint-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { Stack } from '@folio/components/ui/flex';

const FORM_ID = 'drivequant-surcharge-form';

const surchargeRates = [
  axeceDrivequantSurchargeRateEnum.NONE,
  axeceDrivequantSurchargeRateEnum['1.3'],
  axeceDrivequantSurchargeRateEnum['1.5'],
] as const;

type DrivequantSurchargeFormValues = { newDrivequantSurchargeRate: string };

export const DrivequantSurchargeQuickActionDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        newDrivequantSurchargeRate: z.enum(surchargeRates, {
          required_error: t('drivequant.surcharge.levelRequired'),
        }),
      }),
    [t],
  );

  const form = useForm<DrivequantSurchargeFormValues>({
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  // Rate keys hold a dot ('1.3'), which i18next would read as a nested path.
  const rateLabels = t('drivequant.surcharge.rates', { returnObjects: true }) as Record<
    AxeceDrivequantSurchargeRate,
    string
  >;

  const { policy } = usePolicyOnDashboard();
  const queryClient = useQueryClient();
  const { mutateAsync: surchargePolicy } = useAxeceControllerSurchargePolicy();

  const [onSubmit] = useApiCallback(async (v: DrivequantSurchargeFormValues) => {
    await surchargePolicy({
      policyId: policy.id,
      data: {
        newDrivequantSurchargeRate: v.newDrivequantSurchargeRate as AxeceDrivequantSurchargeRate,
      },
    });

    // Surcharge repricing forks, quotes, confirms and merges a branch: every policy query is stale.
    await queryClient.invalidateQueries();

    onClose();
  });

  const oldDrivequantSurchargeRate = policy.customFields
    .newDrivequantSurchargeRate as AxeceDrivequantSurchargeRate;

  return (
    <Dialog open onOpenChange={isOpen => !isOpen && onClose()}>
      <Form {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('drivequant.surcharge.action')}</DialogTitle>
            <DialogDescription>
              {t('drivequant.surcharge.current', {
                rate: rateLabels[oldDrivequantSurchargeRate],
              })}
            </DialogDescription>
          </DialogHeader>
          <Stack asChild>
            <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)}>
              <FormSelectInput
                label={t('drivequant.surcharge.levelLabel')}
                placeholder={t('drivequant.surcharge.levelPlaceholder')}
                name="newDrivequantSurchargeRate"
                required
                control={form.control}
                options={surchargeRates.map(value => ({ label: rateLabels[value], value }))}
              />
            </form>
          </Stack>
          <DialogFooter>
            <Button type="submit" loading={form.formState.isSubmitting} form={FORM_ID}>
              {t('drivequant.surcharge.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Form>
    </Dialog>
  );
};
